import React, { useState } from 'react';

export default function BracketTorneo({ torneo = {}, resultados = [], usuarios = [], esAdmin = false, onRevertir }) {
  // Estado para alternar entre: 'grupos', 'llaves' y 'podio'
  const [vista, setVista] = useState('llaves');

  // Helper de nombres: `completo = true` para podio, `false` para zonas y llaves
  const getNombreJugador = (uid, completo = false) => {
    if (!uid) return 'Socio';
    const u = (usuarios || []).find((usr) => usr && usr.uid === uid);
    if (!u || !u.nombre) return 'Socio';
    
    if (completo) {
      return u.nombre.trim(); // Nombre y Apellido completos
    }

    const partes = u.nombre.trim().split(' ');
    // Muestra hasta dos palabras completas (Ej: "Juan Pablo" o "Juan Pérez")
    if (partes.length > 1) {
      return `${partes[0]} ${partes[1]}`;
    }
    return partes[0];
  };

  const partidosTorneo = (resultados || []).filter((r) => r && r.torneoId === torneo?.id);

  // Separación de partidos por fase
  const partidosZona = partidosTorneo.filter((p) => {
    const f = p && p.fase ? String(p.fase).toLowerCase() : 'zona';
    return f === 'zona' || f.includes('grupo');
  });

  const FASES_LLAVE = ['16avos de Final', '8avos de Final', 'Cuartos de Final', 'Semifinal', 'Final'];
  const partidosLlave = partidosTorneo.filter((p) => {
    const f = p && p.fase ? String(p.fase).toLowerCase() : 'zona';
    return FASES_LLAVE.some((fl) => fl.toLowerCase() === f);
  });

  // Lógica para detectar al Podio (Campeón, Subcampeón y Terceros) con nombres COMPLETOS
  const partidoFinal = partidosTorneo.find((p) => (p?.fase || '').toLowerCase() === 'final');
  const partidosSemi = partidosTorneo.filter((p) => (p?.fase || '').toLowerCase() === 'semifinal');

  let campeon = null;
  let subcampeon = null;
  const terceros = [];

  if (partidoFinal && partidoFinal.ganador) {
    const eq1 = partidoFinal.equipo1.map((id) => getNombreJugador(id, true)).join(' / ');
    const eq2 = partidoFinal.equipo2.map((id) => getNombreJugador(id, true)).join(' / ');
    if (partidoFinal.ganador === 1) {
      campeon = eq1;
      subcampeon = eq2;
    } else {
      campeon = eq2;
      subcampeon = eq1;
    }
  }

  partidosSemi.forEach((p) => {
    if (p.ganador) {
      const perdedorUids = p.ganador === 1 ? p.equipo2 : p.equipo1;
      const nombrePerdedor = perdedorUids.map((id) => getNombreJugador(id, true)).join(' / ');
      if (nombrePerdedor && !terceros.includes(nombrePerdedor)) {
        terceros.push(nombrePerdedor);
      }
    }
  });

  return (
    <div className="space-y-4 my-2">
      {/* ========================================================= */}
      {/* SELECTOR SUPERIOR DE 3 BOTONES                            */}
      {/* ========================================================= */}
      <div className="flex justify-center gap-1 bg-slate-950/80 p-1 rounded-xl border border-white/10 max-w-sm mx-auto shadow-inner">
        <button
          type="button"
          onClick={() => setVista('grupos')}
          className={`flex-1 py-1.5 px-2 rounded-lg text-[9.5px] font-black uppercase tracking-wider transition-all ${
            vista === 'grupos' ? 'bg-cyan-600 text-white shadow' : 'text-slate-400 hover:text-white'
          }`}
        >
          📍 Grupos
        </button>

        <button
          type="button"
          onClick={() => setVista('llaves')}
          className={`flex-1 py-1.5 px-2 rounded-lg text-[9.5px] font-black uppercase tracking-wider transition-all ${
            vista === 'llaves' ? 'bg-purple-600 text-white shadow' : 'text-slate-400 hover:text-white'
          }`}
        >
          🌿 Llaves
        </button>

        <button
          type="button"
          onClick={() => setVista('podio')}
          className={`flex-1 py-1.5 px-2 rounded-lg text-[9.5px] font-black uppercase tracking-wider transition-all ${
            vista === 'podio' ? 'bg-amber-500 text-black shadow font-bold' : 'text-slate-400 hover:text-white'
          }`}
        >
          🏆 Podio
        </button>
      </div>

      {/* ========================================================= */}
      {/* VISTA 1: FASE DE GRUPOS                                    */}
      {/* ========================================================= */}
      {vista === 'grupos' && (
        <div className="bg-slate-950/90 rounded-2xl border border-cyan-500/20 p-3 shadow-xl">
          <div className="flex justify-between items-center mb-2 px-1 pb-2 border-b border-white/5">
            <span className="text-[10px] font-black uppercase text-cyan-400 tracking-wider flex items-center gap-1.5">
              <span>📍</span> Partidos de Grupo / Zona
            </span>
            <span className="text-[9px] font-bold text-slate-500 bg-slate-900 px-2 py-0.5 rounded-full border border-white/5">
              {partidosZona.length} partidos
            </span>
          </div>

          <div className="max-h-72 overflow-auto custom-scrollbar">
            {partidosZona.length === 0 ? (
              <div className="text-center py-6 italic text-slate-500 text-[11px]">
                Sin partidos de Zona o Grupos registrados.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-[290px]">
                {partidosZona.map((partido, idx) => {
                  const eq1 = Array.isArray(partido?.equipo1) ? partido.equipo1 : [];
                  const eq2 = Array.isArray(partido?.equipo2) ? partido.equipo2 : [];
                  const e1Nombres = eq1.map((id) => getNombreJugador(id, false)).join(' / ') || 'Pareja A';
                  const e2Nombres = eq2.map((id) => getNombreJugador(id, false)).join(' / ') || 'Pareja B';
                  const e1Gano = partido?.ganador === 1;
                  const e2Gano = partido?.ganador === 2;

                  return (
                    <div
                      key={partido?.id || idx}
                      className="bg-slate-900/90 border border-slate-800 rounded-xl p-2 text-xs flex justify-between items-center gap-2 shadow-sm"
                    >
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className={`flex justify-between items-center px-2 py-0.5 rounded-lg ${e1Gano ? 'bg-emerald-500/20 text-emerald-300 font-black' : 'text-slate-300'}`}>
                          <span className="whitespace-nowrap font-bold text-[11px] truncate">{e1Nombres}</span>
                          <span className="text-[9px] font-black tabular-nums ml-2 shrink-0">
                            {e1Gano ? '👑 ' : ''}{partido?.puntosEnJuego > 0 && (e1Gano ? `+${partido.puntosEnJuego}` : `-${partido.puntosEnJuego}`)}
                          </span>
                        </div>

                        <div className={`flex justify-between items-center px-2 py-0.5 rounded-lg ${e2Gano ? 'bg-emerald-500/20 text-emerald-300 font-black' : 'text-slate-300'}`}>
                          <span className="whitespace-nowrap font-bold text-[11px] truncate">{e2Nombres}</span>
                          <span className="text-[9px] font-black tabular-nums ml-2 shrink-0">
                            {e2Gano ? '👑 ' : ''}{partido?.puntosEnJuego > 0 && (e2Gano ? `+${partido.puntosEnJuego}` : `-${partido.puntosEnJuego}`)}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col items-end justify-center border-l border-white/5 pl-2 min-w-[55px]">
                        {Array.isArray(partido?.sets) && partido.sets.length > 0 ? (
                          <div className="text-[8px] font-mono text-cyan-300 flex flex-col items-end leading-tight">
                            {partido.sets.map((s, sIdx) => (
                              <span key={sIdx}>{s.p1}-{s.p2}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[8px] text-slate-600 font-bold">VS</span>
                        )}

                        {esAdmin && typeof onRevertir === 'function' && (
                          <button
                            type="button"
                            onClick={() => onRevertir(partido)}
                            className="text-[7.5px] font-bold text-rose-500/80 hover:text-rose-400 mt-1 uppercase"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* VISTA 2: LLAVES ELIMINATORIAS                             */}
      {/* ========================================================= */}
      {vista === 'llaves' && (
        <div className="bg-slate-950/90 rounded-2xl border border-purple-500/20 p-3 shadow-xl">
          <div className="flex justify-between items-center mb-2 px-1 pb-2 border-b border-white/5">
            <span className="text-[10px] font-black uppercase text-purple-400 tracking-wider flex items-center gap-1.5">
              <span>🏆</span> Llave Eliminatoria
            </span>
            <span className="text-[9px] font-bold text-slate-500 bg-slate-900 px-2 py-0.5 rounded-full border border-white/5">
              {partidosLlave.length} partidos
            </span>
          </div>

          <div className="max-h-80 overflow-auto custom-scrollbar">
            {(() => {
              const fasesConPartidos = FASES_LLAVE.filter((fase) =>
                partidosLlave.some((p) => {
                  const fasePartido = p && p.fase ? String(p.fase) : '';
                  return fasePartido.toLowerCase() === fase.toLowerCase();
                })
              );

              if (fasesConPartidos.length === 0) {
                return (
                  <div className="text-center py-6 italic text-slate-500 text-[11px]">
                    Aún no hay encuentros de eliminación directa (Octavos, Cuartos, Semi, Final).
                  </div>
                );
              }

              return (
                <div className="flex gap-4 justify-start min-w-max py-1 px-1">
                  {fasesConPartidos.map((faseNombre) => {
                    const partidosFase = partidosLlave.filter((p) => {
                      const fasePartido = p && p.fase ? String(p.fase) : '';
                      return fasePartido.toLowerCase() === faseNombre.toLowerCase();
                    });

                    return (
                      <div key={faseNombre} className="flex flex-col min-w-[225px] space-y-2">
                        <div className="text-[9px] font-black uppercase text-purple-300 text-center tracking-widest bg-purple-950/60 py-1 px-2 rounded-lg border border-purple-500/30">
                          {faseNombre}
                        </div>

                        <div className="flex flex-col justify-around flex-1 gap-3">
                          {partidosFase.map((partido, pIdx) => {
                            const eq1 = Array.isArray(partido?.equipo1) ? partido.equipo1 : [];
                            const eq2 = Array.isArray(partido?.equipo2) ? partido.equipo2 : [];

                            const e1Nombres = eq1.map((id) => getNombreJugador(id, false)).join(' / ') || 'Pareja A';
                            const e2Nombres = eq2.map((id) => getNombreJugador(id, false)).join(' / ') || 'Pareja B';
                            const e1Gano = partido?.ganador === 1;
                            const e2Gano = partido?.ganador === 2;

                            return (
                              <div
                                key={partido?.id || pIdx}
                                className="bg-slate-900 border border-slate-800 rounded-xl p-2 shadow-md space-y-1 hover:border-purple-500/40 transition-all"
                              >
                                <div
                                  className={`flex justify-between items-center px-2 py-1 rounded-lg ${
                                    e1Gano
                                      ? 'bg-emerald-500/20 text-emerald-300 font-black border border-emerald-500/30'
                                      : 'text-slate-300 font-medium'
                                  }`}
                                >
                                  <span className="whitespace-nowrap text-[11px] font-bold truncate">{e1Nombres}</span>
                                  <div className="flex items-center gap-1 ml-2 shrink-0">
                                    {partido?.puntosEnJuego > 0 && (
                                      <span className={`text-[8.5px] font-black tabular-nums ${e1Gano ? 'text-emerald-400' : 'text-rose-400'}`}>
                                        {e1Gano ? `+${partido.puntosEnJuego}` : `-${partido.puntosEnJuego}`}
                                      </span>
                                    )}
                                    {e1Gano && <span className="text-[10px]">👑</span>}
                                  </div>
                                </div>

                                <div
                                  className={`flex justify-between items-center px-2 py-1 rounded-lg ${
                                    e2Gano
                                      ? 'bg-emerald-500/20 text-emerald-300 font-black border border-emerald-500/30'
                                      : 'text-slate-300 font-medium'
                                  }`}
                                >
                                  <span className="whitespace-nowrap text-[11px] font-bold truncate">{e2Nombres}</span>
                                  <div className="flex items-center gap-1 ml-2 shrink-0">
                                    {partido?.puntosEnJuego > 0 && (
                                      <span className={`text-[8.5px] font-black tabular-nums ${e2Gano ? 'text-emerald-400' : 'text-rose-400'}`}>
                                        {e2Gano ? `+${partido.puntosEnJuego}` : `-${partido.puntosEnJuego}`}
                                      </span>
                                    )}
                                    {e2Gano && <span className="text-[10px]">👑</span>}
                                  </div>
                                </div>

                                <div className="flex justify-between items-center pt-1 border-t border-white/5 text-[8px] px-1">
                                  <div className="text-cyan-400 font-mono font-bold flex gap-1">
                                    {Array.isArray(partido?.sets) && partido.sets.length > 0 ? (
                                      partido.sets.map((s, sIdx) => (
                                        <span key={sIdx} className="bg-slate-950 px-1 rounded border border-white/5">
                                          {s.p1}-{s.p2}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-slate-600">Sin parciales</span>
                                    )}
                                  </div>

                                  {esAdmin && typeof onRevertir === 'function' && (
                                    <button
                                      type="button"
                                      onClick={() => onRevertir(partido)}
                                      className="text-rose-500/80 hover:text-rose-400 font-bold uppercase tracking-wider"
                                    >
                                      Revertir
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* VISTA 3: PODIO DE GANADORES (NOMBRES COMPLETOS)           */}
      {/* ========================================================= */}
      {vista === 'podio' && (
        <div className="bg-slate-950/90 rounded-2xl border border-yellow-500/30 p-5 shadow-2xl text-center space-y-4">
          <div className="flex justify-center items-center gap-2">
            <span className="text-lg">🥇</span>
            <h5 className="text-xs font-black uppercase tracking-[0.2em] text-yellow-400">
              Podio del Torneo
            </h5>
            <span className="text-lg">🥇</span>
          </div>

          {!campeon && !subcampeon && terceros.length === 0 ? (
            <div className="py-6 text-slate-500 text-xs italic">
              El podio se completará automáticamente a medida que se carguen las Semifinales y la Final.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              {/* 🥇 CAMPEÓN */}
              <div className="bg-gradient-to-b from-yellow-500/20 to-yellow-950/40 border border-yellow-500/40 p-4 rounded-xl shadow-lg flex flex-col items-center justify-center order-1 sm:order-2">
                <span className="text-3xl mb-1">🥇</span>
                <span className="text-[9px] font-black text-yellow-500 uppercase tracking-widest">Campeón</span>
                <b className="text-sm font-black text-white uppercase mt-1 leading-snug">{campeon || 'Por definir'}</b>
              </div>

              {/* 🥈 SUBCAMPEÓN */}
              <div className="bg-slate-900/80 border border-slate-400/30 p-3.5 rounded-xl shadow flex flex-col items-center justify-center order-2 sm:order-1">
                <span className="text-2xl mb-1">🥈</span>
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Subcampeón</span>
                <b className="text-xs font-bold text-slate-200 uppercase mt-1 leading-snug">{subcampeon || 'Por definir'}</b>
              </div>

              {/* 🥉 3ER PUESTO */}
              <div className="bg-slate-900/80 border border-amber-700/30 p-3.5 rounded-xl shadow flex flex-col items-center justify-center order-3">
                <span className="text-2xl mb-1">🥉</span>
                <span className="text-[9px] font-black text-amber-600 uppercase tracking-widest">3er Puesto</span>
                <b className="text-xs font-bold text-slate-200 uppercase mt-1 leading-snug">
                  {terceros.length > 0 ? terceros.join(' / ') : 'Por definir'}
                </b>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}